import { SimilarDesignItem } from '@app/data/clients/rbServerSideClient/queries/GetSimilarDesigns/types';
import {
  PreviewStyle,
  SIMILAR_DESIGNS_LIMIT,
  SIMILAR_DESIGNS_SEO_LINK_LIMIT,
} from '@app/pages/i/constants';
import {
  GetSimilarDesignsParameters,
  useSimilarDesigns,
} from '@app/pages/i/hooks/useSimilarDesigns';
import { AppProps, UserInfo } from '@redbubble/next/types';
import { usePageProps, useUserInfo } from '@redbubble/next-provider';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useCurrentInventoryStore } from '../hooks/useCurrentInventoryItem';
import { ProductPageProps } from '../types';
import { getOffsiteCarouselFirstItem } from './offsitePdpHelpers';

export interface Context {
  similarDesignsData: {
    offsiteSimilarDesigns: SimilarDesignItem[];
    similarDesigns: SimilarDesignItem[];
  };
  similarDesignsLoading: boolean;
  refreshSimilarDesigns: (variables: GetSimilarDesignsParameters) => void;
  showOffsiteSimilarDesignsSection: boolean;
}

export const SimilarDesignsContext = createContext<Context | undefined>(
  undefined
);
SimilarDesignsContext.displayName = 'SimilarDesignsContext';

export function SimilarDesignsProvider({
  children,
  isBot,
  groupId,
  isOffsiteVisitor,
}) {
  const [userInfo] = useUserInfo<UserInfo>();
  const { inventoryCategorySet, initialInventoryItem } = usePageProps<
    ProductPageProps & AppProps
  >();

  const { workId, id } = useCurrentInventoryStore(
    (state) => state.currentInventoryItem
  );

  const {
    similarDesigns,
    loading: similarDesignsLoading,
    refresh: refreshSimilarDesigns,
  } = useSimilarDesigns();

  const [initialItem] = useState<SimilarDesignItem | null>(() =>
    getOffsiteCarouselFirstItem(
      initialInventoryItem,
      inventoryCategorySet.defaultItem,
      isOffsiteVisitor
    )
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: useExhaustiveDependencies
  useEffect(() => {
    const { locale, country, currency } = userInfo;
    refreshSimilarDesigns({
      inventoryItemId: id,
      limit: isBot ? SIMILAR_DESIGNS_SEO_LINK_LIMIT : SIMILAR_DESIGNS_LIMIT,
      locale,
      country,
      currency,
      previewTypeIds: [
        groupId
          ? PreviewStyle.ALTERNATE_PRODUCT_CLOSE
          : PreviewStyle.CLOSE_PREVIEW,
      ],
      groupId,
      isBot,
    });
  }, []);

  const similarDesignsData = useMemo(() => {
    const currentItemHasOriginalItemWork =
      initialItem &&
      workId === (initialItem as SimilarDesignItem).inventoryItem?.workId;

    const filteredDesigns = similarDesigns.filter(
      (design) => design.inventoryItem?.workId !== workId
    );

    const getOffsiteSimilarDesigns = () => {
      if (currentItemHasOriginalItemWork) {
        return filteredDesigns.slice(0, 8);
      }
      if (initialItem) {
        return [initialItem, ...filteredDesigns.slice(0, 7)];
      }
      return filteredDesigns.slice(0, 8);
    };

    const offsiteSimilarDesigns = getOffsiteSimilarDesigns();

    return {
      offsiteSimilarDesigns,
      similarDesigns: initialItem ? similarDesigns.slice(8) : filteredDesigns,
    };
  }, [similarDesigns, workId, initialItem]);

  const showOffsiteSimilarDesignsSection = useMemo(() => {
    return !!initialItem || isOffsiteVisitor;
  }, [initialItem, isOffsiteVisitor]);

  const value = useMemo(() => {
    return {
      similarDesignsData,
      similarDesignsLoading,
      refreshSimilarDesigns,
      showOffsiteSimilarDesignsSection,
    };
  }, [
    similarDesignsData,
    similarDesignsLoading,
    refreshSimilarDesigns,
    showOffsiteSimilarDesignsSection,
  ]);

  return (
    <SimilarDesignsContext.Provider value={value}>
      {children}
    </SimilarDesignsContext.Provider>
  );
}

export function useSimilarDesignsContext() {
  const context = useContext(SimilarDesignsContext);

  if (context === undefined) {
    throw new Error(
      `useSimilarDesignsContext must be used within a SimilarDesignsContext Provider`
    );
  }

  return context;
}
